import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { 
  Search, 
  Plus, 
  Bookmark, 
  Linkedin, 
  ChevronRight,
  Filter,
  X,
  Clock,
  Megaphone,
  Target,
  MapPin,
  Users,
  BarChart3,
  Gift,
} from "lucide-react";
import campaignAdvertisingService from "../../company/services/campaignAdvertisingService";

// Helper function to format time ago
const formatTimeAgo = (date) => {
  if (!date) return "Recently";
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);
  
  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return past.toLocaleDateString();
};

const followerRangeMap = {
  Nano: { instagram: '1k - 10k', youtube: '1k - 5k' },
  Micro: { instagram: '10k - 50k', youtube: '5k - 20k' },
  Macro: { instagram: '50k - 500k', youtube: '20k - 100k' },
  Mega: { instagram: '500k - 1M', youtube: '100k - 1M' },
  Celeb: { instagram: '1M+', youtube: '1M+' },
};

const formatFollowerRangeDisplay = (ranges = []) => {
  if (!ranges.length) return "Not specified";
  return ranges
    .map((range) => {
      const detail = followerRangeMap[range];
      if (detail) {
        return `${range} (Instagram: ${detail.instagram} | YouTube: ${detail.youtube})`;
      }
      return range;
    })
    .join(", ");
};

const Campaign = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Recent");
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [budgetFilter, setBudgetFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [bookmarkedCampaigns, setBookmarkedCampaigns] = useState(new Set());
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch published campaign advertisings
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await campaignAdvertisingService.getPublishedCampaignAdvertisings();
        
        if (response.success && response.data) {
          // Transform backend data to match frontend structure
          const transformedCampaigns = response.data.map((item) => {
            const followerRangeDisplay = formatFollowerRangeDisplay(item.followerRange || []);
          
            return {
              id: item.id,
              _id: item.id, // keep for routing compatibility
              // Prefer explicit fields so we can search/filter accurately
              name: item.brandManager?.name || item.brand?.name || "Brand",
              brandManagerName: item.brandManager?.name || "",
              profession: "Brand Manager",
              createdAt: item.createdAt || null,
              location:
                item.location ||
                item.targetLocation ||
                item.country ||
                item.city ||
                "",
              timeAgo: formatTimeAgo(item.createdAt) || "Recently",
              profileImage: `https://picsum.photos/50?random=${item.id.substring(0, 8)}`,
          
              linkedin: item.brandManager?.linkedin || "#",
          
              lookingFor:
                item.hireType === "agency"
                  ? "Influencer Marketing Agencies"
                  : "Content Creators",
          
              description: item.requirement || "",
          
              attributes: {
                brandName: item.brand?.name || item.brandName || "Not disclosed",
                campaignType: item.campaign?.name || "Campaign",
                platform: Array.isArray(item.platform)
                  ? item.platform.join(", ")
                  : item.platform || "Not specified",
                creatorsCategory: item.category || "Not specified",
                followerRange: followerRangeDisplay,
                deliverables: item.deliverables || "Not specified",
                agencyName: item.agencyName || "Not specified",
                budgetRange: item.budget || "Not disclosed",
                location:
                  item.location ||
                  item.targetLocation ||
                  item.country ||
                  item.city ||
                  "Not specified",
              },
          
              hireType: item.hireType,
              status: item.status,
            };
          });
          
          setCampaigns(transformedCampaigns);
        }
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setError(err.message || "Failed to load campaigns");
        // Fallback to empty array on error
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Filter campaigns based on search and filters
  const filteredCampaigns = campaigns.filter((campaign) => {
    const query = searchQuery.trim().toLowerCase();

    // Text search: brand manager, brand, agency, description, campaign type, lookingFor
    if (query) {
      const matchesSearch =
        campaign.name?.toLowerCase().includes(query) ||
        campaign.brandManagerName?.toLowerCase().includes(query) ||
        campaign.attributes.brandName?.toLowerCase().includes(query) ||
        campaign.attributes.agencyName?.toLowerCase().includes(query) ||
        campaign.description?.toLowerCase().includes(query) ||
        campaign.attributes.campaignType?.toLowerCase().includes(query) ||
        campaign.lookingFor?.toLowerCase().includes(query);

      if (!matchesSearch) return false;
    }

    // Platform filter (simple substring match against display string)
    if (platformFilter) {
      const platformText = (campaign.attributes.platform || "").toLowerCase();
      if (!platformText.includes(platformFilter.toLowerCase())) return false;
    }

    // Location filter
    if (locationFilter) {
      const locationText =
        (campaign.location ||
          campaign.attributes.location ||
          "").toLowerCase();
      if (!locationText.includes(locationFilter.toLowerCase())) return false;
    }

    // Budget filter: interpret as simple buckets based on numeric value if present
    if (budgetFilter) {
      const budgetText = String(campaign.attributes.budgetRange || "");
      const numbers = budgetText.match(/\d+/g);
      const approxValue = numbers && numbers.length ? parseInt(numbers[0], 10) : null;

      if (approxValue !== null) {
        if (budgetFilter === "UNDER_50000" && !(approxValue < 50000)) {
          return false;
        }
        if (budgetFilter === "50000_100000" && !(approxValue >= 50000 && approxValue <= 100000)) {
          return false;
        }
        if (budgetFilter === "ABOVE_100000" && !(approxValue > 100000)) {
          return false;
        }
      }
    }

    // Start date filter: use createdAt as a proxy
    if (startDateFilter) {
      const campaignDate = campaign.createdAt ? new Date(campaign.createdAt) : null;
      const selectedDate = new Date(startDateFilter);
      if (!campaignDate || campaignDate < selectedDate) return false;
    }

    return true;
  });


const trendingCampaigns = [
  {
    id: 1,
    title: "Food Blogger Collab",
    subtitle: "Eatsızixy",
    // Gets a 200x150 random image
    image: "https://picsum.photos/200/150?random=4"
  },
  {
    id: 2,
    title: "Tech Gadget Review",
    subtitle: "Fashionxest",
    image: "https://picsum.photos/200/150?random=5"
  },
  {
    id: 3,
    title: "Sustainable Week",
    subtitle: "Eco Living",
    image: "https://picsum.photos/200/150?random=6"
  }
];

  const toggleBookmark = (campaignId) => {
    setBookmarkedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  const tabs = ["Recent", "My Requests", "Deals you missed"];

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col overflow-x-hidden">
      <Navbar />
      <div className="flex-grow pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-2 md:px-4 w-full">
          {/* Header Section */}
          <div className="flex flex-nowrap items-center justify-between mb-6 gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Campaigns</h1>
            <button className="bg-orange-500 hover:bg-orange-600 text-white px-2 md:px-4 py-1 md:py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0">
              My Activity
            </button>
          </div>

          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Column - Main Content */}
            <div className="flex-1 min-w-0">
              {/* Search Bar */}
              <div className="flex gap-3 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search for campaigns by name or description"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-2 md:py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <button className="bg-orange-500 hover:bg-orange-600 text-white p-2 md:p-3 rounded-lg transition-colors flex-shrink-0">
                  <Plus className="w-6 h-6" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-2 mb-6 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-2 md:px-6 py-1 md:py-2.5 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab
                        ? "bg-orange-500 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Campaign Cards */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading campaigns...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredCampaigns.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No campaigns found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCampaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    onClick={() => navigate(`/campaign/${campaign._id || campaign.id}`)}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-2 md:p-4 cursor-pointer"
                  >
                    {/* Profile Section */}
                    <div className="flex items-start gap-4 mb-5">
                      <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        <img
                          src={campaign.profileImage}
                          alt={campaign.name}
                          className="w-full h-full object-cover max-w-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base md:text-lg font-bold text-gray-900">{campaign.name}</h3>
                          <a
                            href={campaign.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                          >
                            <Linkedin className="w-5 h-5" />
                          </a>
                        </div>
                        <p className="text-xs md:text-sm text-gray-600 mb-1">{campaign.profession}</p>
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{campaign.timeAgo}</span>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Header */}
                    <div className="mb-4">
                      <p className="text-xs md:text-sm text-gray-600 mb-1">Looking for</p>
                      <div className="flex items-start justify-between gap-4">
                        <h2 className="text-xl font-bold text-gray-900 flex-1">
                          {campaign.lookingFor}
                        </h2>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBookmark(campaign.id);
                          }}
                          className="text-orange-500 hover:text-orange-600 transition-colors flex-shrink-0 mt-1"
                        >
                          <Bookmark
                            className={`w-5 h-5 ${
                              bookmarkedCampaigns.has(campaign.id) ? "fill-current" : ""
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-xs md:text-sm text-gray-700 mt-2">{campaign.description}</p>
                    </div>

                    {/* Detailed Attributes */}
                    <div className="space-y-2 md:space-y-3 pt-2 md:pt-4 border-t border-gray-100">
                      {/* Brand Name */}
                      <div className="flex items-start gap-3">
                        <Megaphone className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Brand Name: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.brandName}</span>
                        </div>
                      </div>

                      {/* Campaign Type */}
                      <div className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Campaign Type: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.campaignType}</span>
                        </div>
                      </div>

                      {/* Platform */}
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Platform: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.platform}</span>
                        </div>
                      </div>

                      {/* Creators Category */}
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Creators Category: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.creatorsCategory}</span>
                        </div>
                      </div>

                      {/* Follower Range */}
                      <div className="flex items-start gap-3">
                        <BarChart3 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Follower Range: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.followerRange}</span>
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="flex items-start gap-3">
                        <Gift className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Deliverables: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.deliverables}</span>
                        </div>
                      </div>
                       {/* Agency Name */}
                       <div className="flex items-start gap-3">
                        <Gift className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Agency Name: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.agencyName}</span>
                        </div>
                      </div>

                      {/* Budget Range */}
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center text-gray-500 flex-shrink-0 mt-0.5">
                          <span className="text-base md:text-lg font-semibold">₹</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-xs md:text-sm text-gray-600">Budget Range: </span>
                          <span className="text-xs md:text-sm font-medium text-gray-900">{campaign.attributes.budgetRange}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Filters & Trending (Desktop/Tablet) */}
            <div className="hidden md:block lg:w-80 flex-shrink-0 space-y-6">
              {/* Filters Section */}
              <div className="bg-white rounded-xl shadow-sm p-2 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                </div>

                <div className="space-y-2 md:space-y-4">
                  {/* Campaign Type */}
                  <div>
                    <button className="w-full flex items-center justify-between text-gray-700 hover:text-orange-500 transition-colors">
                      <span className="text-xs md:text-sm font-medium">Campaign Type</span>
                      <ChevronRight className="w-5 h-5 text-orange-500" />
                    </button>
                    <button className="w-full flex items-center justify-between text-gray-600 mt-2 pl-4 hover:text-orange-500 transition-colors">
                      <span className="text-xs md:text-sm">Campaign</span>
                      <ChevronRight className="w-4 h-4 text-orange-500" />
                    </button>
                  </div>

                  {/* Platform */}
                  <div>
                    <span className="text-xs md:text-sm font-medium text-gray-700">Platform</span>
                    <select
                      value={platformFilter}
                      onChange={(e) => setPlatformFilter(e.target.value)}
                      className="mt-2 w-full px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                    >
                      <option value="">All Platforms</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                      <option value="facebook">Facebook</option>
                      <option value="tiktok">TikTok</option>
                      <option value="linkedin">LinkedIn</option>
                    </select>
                  </div>

                  {/* Location */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs md:text-sm font-medium text-gray-700">Location</span>
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                      />
                    </div>
                    <select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                    >
                      <option value="">All Locations</option>
                      <option value="India">India</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                    </select>
                  </div>

                  {/* Budget Range */}
                  <div>
                    <span className="text-xs md:text-sm font-medium text-gray-700">Budget Range</span>
                    <select
                      value={budgetFilter}
                      onChange={(e) => setBudgetFilter(e.target.value)}
                      className="mt-2 w-full px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                    >
                      <option value="">All Budgets</option>
                      <option value="UNDER_50000">Under ₹50k</option>
                      <option value="50000_100000">₹50k - ₹1L</option>
                      <option value="ABOVE_100000">Above ₹1L</option>
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                      <span className="text-xs md:text-sm font-medium text-gray-700">Start Date</span>
                    <div className="mt-2">
                      <input
                        type="date"
                        value={startDateFilter}
                        onChange={(e) => setStartDateFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Trending Campaigns Section */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Trending Campaigns</h2>
                <div className="flex md:flex-col gap-4 overflow-x-auto pb-2 md:pb-0">
                  {trendingCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex-shrink-0 w-48 md:w-full cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <div className="rounded-lg overflow-hidden mb-2">
                        <img
                          src={campaign.image}
                          alt={campaign.title}
                          className="w-full h-32 object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-xs md:text-sm">{campaign.title}</h3>
                      <p className="text-gray-500 text-xs mt-1">{campaign.subtitle}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Filter Button */}
          <button
            onClick={() => setShowFilters(true)}
            className="md:hidden fixed bottom-6 right-6 bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-full shadow-lg z-40 transition-transform hover:scale-110"
          >
            <Filter className="w-6 h-6" />
          </button>

          {/* Mobile Filter Overlay */}
          {showFilters && (
            <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
              <div className="bg-white w-full max-h-[80vh] rounded-t-2xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Filters</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-6">
                  {/* Same filter content as desktop */}
                  <div className="space-y-4">
                    <div>
                      <button className="w-full flex items-center justify-between text-gray-700 hover:text-orange-500 transition-colors">
                        <span className="text-xs md:text-sm font-medium">Campaign Type</span>
                        <ChevronRight className="w-5 h-5 text-orange-500" />
                      </button>
                      <button className="w-full flex items-center justify-between text-gray-600 mt-2 pl-4 hover:text-orange-500 transition-colors">
                        <span className="text-xs md:text-sm">Campaign</span>
                        <ChevronRight className="w-4 h-4 text-orange-500" />
                      </button>
                    </div>

                    <div>
                      <span className="text-xs md:text-sm font-medium text-gray-700">Platform</span>
                      <select
                        value={platformFilter}
                        onChange={(e) => setPlatformFilter(e.target.value)}
                        className="mt-2 w-full px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                      >
                        <option value="">All Platforms</option>
                        <option value="instagram">Instagram</option>
                        <option value="youtube">YouTube</option>
                        <option value="facebook">Facebook</option>
                        <option value="tiktok">TikTok</option>
                        <option value="linkedin">LinkedIn</option>
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-700">Location</span>
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                        />
                      </div>
                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                      >
                        <option value="">All Locations</option>
                        <option value="India">India</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                      </select>
                    </div>

                    <div>
                      <span className="text-xs md:text-sm font-medium text-gray-700">Budget Range</span>
                      <select
                        value={budgetFilter}
                        onChange={(e) => setBudgetFilter(e.target.value)}
                        className="mt-2 w-full px-2 md:px-4 py-1 md:py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                      >
                        <option value="">All Budgets</option>
                        <option value="UNDER_50000">Under ₹50k</option>
                        <option value="50000_100000">₹50k - ₹1L</option>
                        <option value="ABOVE_100000">Above ₹1L</option>
                      </select>
                    </div>

                    <div>
                      <span className="text-xs md:text-sm font-medium text-gray-700">Start Date</span>
                      <div className="mt-2">
                        <input
                          type="date"
                          value={startDateFilter}
                          onChange={(e) => setStartDateFilter(e.target.value)}
                          className="w-full px-2 md:px-4 py-1 md:py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Campaign;
